// src/pages/markets/board/card/CardSkeleton.tsx
//
// Same three blocks as the card, same heights, so the swap from skeleton to
// content moves nothing. The ladder is the tall block now — the skeleton shows
// rows rather than one slab, because that is what arrives.

import { Box, Skeleton } from '@mui/material';
import {
    BODY_HEIGHT,
    BODY_PADDING_Y,
    BLOCK_GAP,
    CARD_HEIGHT,
    CARD_TEST_IDS,
    CONVICTION_ROW_HEIGHT,
    FOOTER_HEIGHT,
    HEADER_HEIGHT,
    MARKET_BLOCK_HEIGHT,
    OUTCOME_ROW_HEIGHT,
} from './cardLayout';

const LADDER_ROWS = Math.floor(MARKET_BLOCK_HEIGHT / OUTCOME_ROW_HEIGHT);

export function CardSkeleton() {
    return (
        <Box
            data-testid={CARD_TEST_IDS.root}
            style={{ height: CARD_HEIGHT }}
            sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
                borderRadius: '4px',
                boxShadow: theme => `inset 0 0 0 1px ${theme.palette.divider}`,
            }}
        >
            <Box
                data-testid={CARD_TEST_IDS.header}
                style={{ height: HEADER_HEIGHT }}
                sx={{ px: 1.25, pt: 0.75, pb: 0.5, boxSizing: 'border-box' }}
            >
                <Skeleton variant="text" width="90%" sx={{ fontSize: '13px' }} />
                <Skeleton variant="text" width="45%" sx={{ fontSize: '11px' }} />
            </Box>

            <Box
                data-testid={CARD_TEST_IDS.body}
                style={{
                    height: BODY_HEIGHT,
                    paddingTop: BODY_PADDING_Y,
                    paddingBottom: BODY_PADDING_Y,
                }}
                sx={{ px: 1.25, boxSizing: 'border-box' }}
            >
                <Box
                    style={{ height: MARKET_BLOCK_HEIGHT, marginBottom: BLOCK_GAP }}
                    sx={{ boxSizing: 'border-box', overflow: 'hidden' }}
                >
                    {Array.from({ length: LADDER_ROWS }, (_, i) => (
                        <Box
                            key={i}
                            style={{ height: OUTCOME_ROW_HEIGHT }}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            {/* Widths taper: a ladder is sorted, and a skeleton
                                that ignores that reads as a table. */}
                            <Skeleton variant="text" sx={{ flex: 1, fontSize: '11px' }} />
                            <Skeleton variant="rounded" width={26} height={4} />
                            <Skeleton variant="text" width={28} sx={{ fontSize: '11px' }} />
                            <Skeleton variant="text" width={46} sx={{ fontSize: '11px' }} />
                        </Box>
                    ))}
                </Box>

                <Box
                    data-testid={CARD_TEST_IDS.conviction}
                    style={{ height: CONVICTION_ROW_HEIGHT }}
                    sx={{
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Skeleton variant="text" width={64} sx={{ fontSize: '12px' }} />
                    <Skeleton variant="text" width={52} sx={{ fontSize: '11px' }} />
                    <Skeleton variant="text" width={90} sx={{ fontSize: '11px' }} />
                </Box>
            </Box>

            <Box
                data-testid={CARD_TEST_IDS.footer}
                style={{ height: FOOTER_HEIGHT }}
                sx={{
                    px: 1.25,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <Skeleton variant="text" width={60} sx={{ fontSize: '11px' }} />
            </Box>
        </Box>
    );
}